import * as React from 'react';
import { storyblokEditable } from 'gatsby-source-storyblok';
import Container from '../Container';
import FancyTitle from '../fancy-title';
import StoryBlokLink from './StoryBlokLink';
import {
  renderReferenceRichText,
  StoryblokResponsiveImage,
  hasStoryblokLink,
} from './reference-section-helpers';

/**
 * @param {{
 *   blok: any,
 *   sectionClassName?: string,
 *   titleTag?:  'h1' | 'h2' | 'h3',
 *   imageSizes?: string,
 *   desktopGridClass?: string,
 *   mobileTitleFirst?: boolean
 * }} props
 */

const ALLOWED_TITLE_TAGS = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'];
const BULLET_TITLE_STYLES = {
  default: 'text-[16px] leading-[24px]',
  emphasis: 'text-[16px] leading-[24px] font-semibold',
  lead: 'text-[18px] leading-[26px] font-semibold',
  accent: 'text-[16px] leading-[24px] font-semibold text-latori-green-dark',
};

export default function ReferenceMediaTextSection(props) {
  const {
    blok,
    sectionClassName = '',
    titleTag = ALLOWED_TITLE_TAGS.includes(blok?.titleTag)
      ? blok.titleTag
      : 'h2',
    imageSizes = '(max-width: 1024px) 100vw, 42vw',
    desktopGridClassRight = 'lg:grid-cols-[1.1fr_0.9fr]',
    desktopGridClassLeft = 'lg:grid-cols-[0.9fr_1.1fr]',
    mobileTitleFirst = false,
  } = props;

  // The editor's choice wins; titleTag is the per-section fallback.
  const requestedTag = String(blok?.headline_level || '').toLowerCase();
  const resolvedTitleTag = ALLOWED_TITLE_TAGS.includes(requestedTag)
    ? requestedTag
    : titleTag;
  const points = /** @type {Array<{ _uid?: string, text?: string }>} */ (
    Array.isArray(blok?.bullet_points) ? blok.bullet_points : []
  );
  const bulletPointsTitle =
    typeof blok?.bullet_points_title === 'string'
      ? blok.bullet_points_title.trim()
      : '';
  const bulletPointsTitleStyle =
    BULLET_TITLE_STYLES[blok?.bullet_points_title_style] ||
    BULLET_TITLE_STYLES.default;
  const desktopImagePosition =
    blok?.desktop_image_position === 'left' ? 'left' : 'right';
  const hasButton =
    Boolean(blok?.button_label?.trim()) && hasStoryblokLink(blok?.button_link);

  const desktopGridClass =
    desktopImagePosition === 'left'
      ? desktopGridClassLeft
      : desktopGridClassRight;
  const imageDesktopCol =
    desktopImagePosition === 'left' ? 'lg:col-start-1' : 'lg:col-start-2';
  const textDesktopCol =
    desktopImagePosition === 'left' ? 'lg:col-start-2' : 'lg:col-start-1';
  // Opt-in per section: only the intro section shows the title above the image.
  const titleMobileOrder = mobileTitleFirst ? 'order-1' : 'order-2';
  const imageMobileOrder = mobileTitleFirst ? 'order-2' : 'order-1';

  return (
    <section {...storyblokEditable(blok)} className={sectionClassName}>
      <Container>
        <div
          className={`grid grid-cols-1 gap-8 ${desktopGridClass} lg:gap-x-12 lg:gap-y-0 items-start`}>
          {blok?.title && (
            <div
              className={`${titleMobileOrder} ${textDesktopCol} lg:row-start-1`}>
              {/* Heading size is provided by REFERENCE_HEADING_SCALE through
                  HeadingScaleProvider (src/components/typography/heading-scale.js). */}
              <FancyTitle
                text={blok.title}
                tag={resolvedTitleTag}
                textAlign='text-left'
                marginBottom='mb-0 lg:mb-6'
              />
            </div>
          )}
          <div
            className={`${imageMobileOrder} ${imageDesktopCol} lg:row-start-1 lg:row-span-2 w-full`}>
            <StoryblokResponsiveImage
              image={blok?.image}
              alt={blok?.title}
              className='w-full h-full object-cover'
              sizes={imageSizes}
            />
          </div>

          <div className={`order-3 ${textDesktopCol} lg:row-start-2`}>
            {renderReferenceRichText(
              blok?.text,
              'mt-4 text-[16px] leading-[24px]',
            )}

            {hasButton && (
              <div className='mt-7'>
                <StoryBlokLink
                  link={blok.button_link}
                  className='button-primary inline-flex'>
                  {blok.button_label}
                </StoryBlokLink>
              </div>
            )}
          </div>
        </div>

        {points.length > 0 && (
          <div className='mt-10 lg:mt-12'>
            {bulletPointsTitle && (
              <p className={`mb-4 lg:mb-5 ${bulletPointsTitleStyle}`}>
                {bulletPointsTitle}
              </p>
            )}
            <ul className='columns-1 lg:columns-2 lg:gap-10 list-disc list-outside ml-5 marker:text-latori-green-dark marker:text-lg text-[16px] leading-[24px]'>
              {points.map((point, index) => {
                const key = point?._uid || `${point?.text || 'point'}-${index}`;
                return (
                  <li
                    key={key}
                    className={`${index > 0 ? 'mt-3 lg:mt-2' : ''} break-inside-avoid`}>
                    {point?.text}
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </Container>
    </section>
  );
}